import { Meeting, MeetingFilters } from '@/types/meetings';
import { env } from '@/config/environment';
import { clinicCacheService } from './clinicCacheService';
import { getAccessToken } from './apiService';

const API_URL = env.apiUrl;

/**
 * Build request headers including the caller's Supabase JWT when available.
 * The /api/meetings* endpoints derive clinic_id server-side from this token
 * for the Elo merge — the query string is intentionally NOT trusted.
 *
 * Uses apiService's `getAccessToken()` which reads from the in-memory token
 * cache populated by AuthContext on every session change. This avoids
 * `supabase.auth.getSession()` entirely (which can stall indefinitely on
 * cold starts or token refresh) and gives an instant, reliable token the
 * moment the user is authenticated. Race against a short timeout so a
 * truly hung refresh never blocks the meetings fetch — falls back to an
 * anonymous request, and the silent refetch (onClinicReady) will pick up
 * the merge on the next pass.
 */
async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const tokenPromise = getAccessToken();
    const timeoutPromise = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), 3000)
    );
    const token = await Promise.race([tokenPromise, timeoutPromise]);
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch {
    // Silently fall back to anonymous — caller still gets the public catalog.
  }
  return headers;
}

/**
 * Fetch with an enforced timeout. RN's fetch has no default timeout, so a
 * stalled request would hang forever, leaving the meetings screen stuck on
 * the skeleton. 20s is generous for a 200KB JSON payload over slow mobile.
 */
async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs = 20000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve the current user's clinic_id query string for the Elo merge.
 *
 * Fully non-blocking: meetings are a public endpoint and the screen mounts
 * before AuthContext init completes (~8s cold). We must NEVER add latency to
 * the meetings fetch. Strategy:
 *   - If we have already resolved the clinic id once this session, return it
 *     synchronously (zero delay).
 *   - Otherwise, kick off resolution in the background and return '' so the
 *     server falls back to Supabase-only behaviour (identical to pre-Elo).
 *     Once the background resolution lands, subsequent calls (pull-to-refresh,
 *     next navigation) will include clinic_id and enable the Elo merge.
 */
let cachedClinicQuery: string | null = null;
let inflightResolve: Promise<void> | null = null;
type ClinicReadyListener = (clinicQuery: string) => void;
const clinicReadyListeners = new Set<ClinicReadyListener>();

function notifyClinicReady(query: string) {
  clinicReadyListeners.forEach((cb) => {
    try { cb(query); } catch { /* listener errors must never break warm */ }
  });
}

function warmClinicQuery(): void {
  if (inflightResolve) return;
  inflightResolve = (async () => {
    try {
      const data = await clinicCacheService.getUserClinicData();
      const id = data?.id;
      const query = id ? `?clinic_id=${encodeURIComponent(String(id))}` : '';
      const wasUnknown = cachedClinicQuery === null;
      cachedClinicQuery = query;
      // Only notify when we *gained* a clinic context — not when we resolved
      // to "no clinic" (anonymous user). Listeners use this to refetch and
      // bring in the Elo merge.
      if (wasUnknown && query) notifyClinicReady(query);
    } catch {
      // Leave cachedClinicQuery as null so we'll retry on the next call.
    } finally {
      inflightResolve = null;
    }
  })();
}

function resolveClinicIdQuery(): string {
  if (cachedClinicQuery !== null) return cachedClinicQuery;
  warmClinicQuery();
  return '';
}

/**
 * Subscribe to be notified the first time the user's clinic_id becomes known.
 * Used by the meetings screen to refetch once Elo merge becomes possible
 * (the initial cold-load fires before AuthContext warms up).
 *
 * Returns an unsubscribe function.
 */
export function onClinicReady(cb: ClinicReadyListener): () => void {
  // If already known and non-empty, fire on next tick so the caller can
  // unsubscribe synchronously without missing the event.
  if (cachedClinicQuery) {
    const known = cachedClinicQuery;
    setTimeout(() => cb(known), 0);
    return () => {};
  }
  clinicReadyListeners.add(cb);
  // Kick off the warm so passive subscribers still trigger resolution.
  warmClinicQuery();
  return () => clinicReadyListeners.delete(cb);
}

function appendQuery(url: string, append: string): string {
  if (!append) return url;
  // append already starts with '?'; if url already has '?', convert
  return url.includes('?') ? `${url}&${append.slice(1)}` : `${url}${append}`;
}

// Phase 3b (May 2026): the server no longer dual-writes the legacy flat
// `meeting_time` / `meeting_weekday` fields. We derive them once here from
// `next_session` (+ `sessions[]` for multi-day weekday strings) so the
// 5 legacy consumers (encontros-online, MeetingsCarousel, ChatMeetingsCarousel,
// MeetingDetailsDrawer, CommunityMeetingsSection) keep working unchanged.
// Future refactors can migrate each consumer to read `next_session` directly
// and then this shim can be deleted.
const WEEKDAY_KEY_TO_LEGACY_LABEL: Record<string, string> = {
  segunda: 'segunda-feira', terca: 'terça-feira', quarta: 'quarta-feira',
  quinta: 'quinta-feira',   sexta: 'sexta-feira', sabado: 'sábado',
  domingo: 'domingo',
};
const WEEKDAY_ORDER: Record<string, number> = {
  segunda: 1, terca: 2, quarta: 3, quinta: 4, sexta: 5, sabado: 6, domingo: 7,
};

function deriveLegacyWeekday(m: Meeting): string {
  // Prefer the full session list so multi-day meetings render every weekday
  // in the detail drawer's "Programação" line; fall back to next_session.
  if (Array.isArray(m.sessions) && m.sessions.length > 0) {
    const seen = new Set<string>();
    const labels = m.sessions
      .map(s => s.weekday)
      .filter((w): w is string => !!w && !seen.has(w) && (seen.add(w), true))
      .sort((a, b) => (WEEKDAY_ORDER[a] || 99) - (WEEKDAY_ORDER[b] || 99))
      .map(w => WEEKDAY_KEY_TO_LEGACY_LABEL[w] || w);
    if (labels.length > 0) return labels.join(';');
  }
  const key = m.next_session?.weekday;
  return key ? (WEEKDAY_KEY_TO_LEGACY_LABEL[key] || key) : '';
}

function normalizeMeetingFromApi(m: Meeting | null | undefined): Meeting | null {
  if (!m) return m ?? null;
  // Only backfill when the server omits them (Phase 3b removed the dual-write
  // for the parent-meeting endpoints; Elo-sourced rows still carry the legacy
  // fields populated and are left untouched).
  const needsTime = !m.meeting_time;
  const needsWeekday = !m.meeting_weekday;
  if (!needsTime && !needsWeekday) return m;
  return {
    ...m,
    meeting_time: needsTime
      ? (m.next_session?.time || m.sessions?.[0]?.time || '')
      : m.meeting_time,
    meeting_weekday: needsWeekday ? deriveLegacyWeekday(m) : m.meeting_weekday,
  };
}

export class MeetingsService {
  
  /**
   * Get all meetings via API (bypasses Supabase client timing issues)
   * CRITICAL FIX: Uses server API instead of direct Supabase queries
   */
  static async getAllMeetings(): Promise<Meeting[]> {
    try {
      console.log('📅 MeetingsService: Loading meetings via API...');
      const clinicQuery = await resolveClinicIdQuery();
      const url = appendQuery(`${API_URL}/api/meetings`, clinicQuery);
      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error fetching meetings:', response.status, errorText);
        throw new Error(`Failed to fetch meetings: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MeetingsService: Loaded', data.meetings?.length || 0, 'meetings');
      return (data.meetings || []).map(normalizeMeetingFromApi).filter((m: Meeting | null): m is Meeting => m !== null);
    } catch (error) {
      console.error('❌ Failed to fetch meetings:', error);
      return [];
    }
  }

  /**
   * Get meetings by filters - applies filters client-side after fetching all meetings
   * For performance, we fetch all meetings once and filter in memory
   */
  static async getMeetingsByFilters(filters: MeetingFilters): Promise<Meeting[]> {
    try {
      // Fetch all meetings first
      const allMeetings = await this.getAllMeetings();
      
      let filteredData = allMeetings;

      // Apply time filter
      if (filters.times && filters.times.length > 0) {
        filteredData = filteredData.filter(meeting => 
          filters.times!.includes(meeting.meeting_time)
        );
      }

      // Apply day filter with proper weekday mapping
      if (filters.days && filters.days.length > 0) {
        const dayMapping: Record<string, string> = {
          'segunda': 'segunda-feira',
          'terça': 'terça-feira', 
          'quarta': 'quarta-feira',
          'quinta': 'quinta-feira',
          'sexta': 'sexta-feira',
          'sábado': 'sábado',
          'domingo': 'domingo'
        };

        filteredData = filteredData.filter((meeting: Meeting) => {
          return filters.days!.some(day => {
            const fullDayName = dayMapping[day.toLowerCase()] || day.toLowerCase();
            return (meeting.meeting_weekday || '').toLowerCase().includes(fullDayName);
          });
        });
      }

      // Apply period filter
      if (filters.period) {
        filteredData = filteredData.filter(meeting => 
          meeting.meeting_period === filters.period
        );
      }

      // Apply category filter
      if (filters.category) {
        filteredData = filteredData.filter(meeting => 
          meeting.meeting_category === filters.category
        );
      }

      // Apply region filter
      if (filters.region) {
        filteredData = filteredData.filter(meeting => 
          meeting.meeting_region === filters.region
        );
      }

      // Apply search filter
      if (filters.search && filters.search.length >= 3) {
        const searchLower = filters.search.toLowerCase();
        filteredData = filteredData.filter(meeting => 
          meeting.meeting_name.toLowerCase().includes(searchLower) ||
          meeting.meeting_category?.toLowerCase().includes(searchLower) ||
          meeting.meeting_place?.toLowerCase().includes(searchLower) ||
          meeting.meeting_region?.toLowerCase().includes(searchLower)
        );
      }

      return filteredData;
    } catch (error) {
      console.error('Failed to fetch filtered meetings:', error);
      return [];
    }
  }

  /**
   * Get a single meeting by ID via API (bypasses Supabase client timing issues)
   * CRITICAL FIX: Uses dedicated server API instead of fetching all meetings
   */
  static async getMeetingById(id: string): Promise<Meeting | null> {
    try {
      console.log('📅 MeetingsService: Loading meeting via API:', id);
      const clinicQuery = await resolveClinicIdQuery();
      const url = appendQuery(`${API_URL}/api/meetings/${id}`, clinicQuery);
      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('📅 Meeting not found:', id);
          return null;
        }
        const errorText = await response.text();
        console.error('❌ Error fetching meeting:', response.status, errorText);
        throw new Error(`Failed to fetch meeting: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MeetingsService: Loaded meeting:', id);
      return normalizeMeetingFromApi(data.meeting);
    } catch (error) {
      console.error('Failed to fetch meeting by ID:', error);
      return null;
    }
  }

  /**
   * Get a single meeting with its group data via API
   * Returns both meeting and group in a single request
   */
  static async getMeetingWithGroup(id: string): Promise<{ meeting: Meeting | null; group: any | null; sessions: any[] }> {
    try {
      console.log('📅 MeetingsService: Loading meeting with group via API:', id);
      const clinicQuery = await resolveClinicIdQuery();
      const url = appendQuery(`${API_URL}/api/meetings/${id}`, clinicQuery);
      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('📅 Meeting not found:', id);
          return { meeting: null, group: null, sessions: [] };
        }
        const errorText = await response.text();
        console.error('❌ Error fetching meeting:', response.status, errorText);
        throw new Error(`Failed to fetch meeting: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MeetingsService: Loaded meeting with group:', id, 'hasGroup:', !!data.group, 'sessions:', (data.sessions || []).length);
      return { 
        meeting: normalizeMeetingFromApi(data.meeting),
        group: data.group || null,
        sessions: Array.isArray(data.sessions) ? data.sessions : [],
      };
    } catch (error) {
      console.error('Failed to fetch meeting with group:', error);
      return { meeting: null, group: null, sessions: [] };
    }
  }

  /**
   * Get personalised meeting suggestions via the PRD §6 two-layer engine.
   * Returns up to `limit` parent meetings, each with `sessions[]` + `next_session`.
   * Server caches per-user (TTL 15 min); the client should treat the result as
   * authoritative and not re-rank.
   */
  static async getSuggestions(limit: number = 5): Promise<Meeting[]> {
    try {
      const url = `${API_URL}/api/meetings/suggestions?limit=${limit}`;
      // x-app-version lets the server branch the payload shape for older
      // clients that may not understand `sessions[]` / `next_session`. The
      // header is best-effort: a missing or unparseable value falls back to
      // the legacy-compat shape (which also includes meeting_time + meeting_weekday).
      const Constants = (await import('expo-constants')).default as any;
      const appVersion = Constants?.expoConfig?.version || Constants?.manifest?.version || '0.0.0';
      const headers = {
        ...(await buildAuthHeaders()),
        'x-app-version': String(appVersion),
      };
      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers,
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error fetching meeting suggestions:', response.status, errorText);
        return [];
      }
      const data = await response.json();
      return Array.isArray(data.meetings)
        ? data.meetings.map(normalizeMeetingFromApi).filter((m: Meeting | null): m is Meeting => m !== null)
        : [];
    } catch (error) {
      console.error('❌ Failed to fetch meeting suggestions:', error);
      return [];
    }
  }

  /**
   * Get meeting groups via API
   */
  static async getMeetingGroups(groupIds?: string[]): Promise<any[]> {
    try {
      console.log('📅 MeetingsService: Loading meeting groups via API...');
      let url = `${API_URL}/api/meetings/groups`;
      if (groupIds && groupIds.length > 0) {
        url += `?ids=${groupIds.join(',')}`;
      }
      const clinicQuery = await resolveClinicIdQuery();
      url = appendQuery(url, clinicQuery);

      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error fetching meeting groups:', response.status, errorText);
        throw new Error(`Failed to fetch meeting groups: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MeetingsService: Loaded', data.groups?.length || 0, 'groups');
      return data.groups || [];
    } catch (error) {
      console.error('❌ Failed to fetch meeting groups:', error);
      return [];
    }
  }

  /**
   * Get a single meeting group by slug via API
   */
  static async getGroupBySlug(slug: string): Promise<any | null> {
    try {
      console.log('📅 MeetingsService: Loading group by slug via API:', slug);
      const response = await fetchWithTimeout(`${API_URL}/api/meetings/groups?slug=${encodeURIComponent(slug)}`, {
        method: 'GET',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('📅 Group not found:', slug);
          return null;
        }
        const errorText = await response.text();
        console.error('❌ Error fetching group by slug:', response.status, errorText);
        throw new Error(`Failed to fetch group: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MeetingsService: Loaded group by slug:', slug);
      return data.group || null;
    } catch (error) {
      console.error('❌ Failed to fetch group by slug:', error);
      return null;
    }
  }

  /**
   * Get meetings for a specific group via API
   */
  static async getMeetingsByGroupId(groupId: string): Promise<Meeting[]> {
    try {
      console.log('📅 MeetingsService: Loading meetings for group via API:', groupId);
      const clinicQuery = await resolveClinicIdQuery();
      const url = appendQuery(
        `${API_URL}/api/meetings?group_id=${encodeURIComponent(groupId)}`,
        clinicQuery,
      );
      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: await buildAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error fetching meetings by group:', response.status, errorText);
        throw new Error(`Failed to fetch meetings: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ MeetingsService: Loaded', data.meetings?.length || 0, 'meetings for group', groupId);
      return (data.meetings || [])
        .map(normalizeMeetingFromApi)
        .filter((m: Meeting | null): m is Meeting => m !== null);
    } catch (error) {
      console.error('❌ Failed to fetch meetings by group:', error);
      return [];
    }
  }

  static getUniqueTimeSlots(meetings: Meeting[]): string[] {
    const times = meetings.map(meeting => meeting.meeting_time);
    return [...new Set(times)].sort();
  }

  static getUniqueDays(meetings: Meeting[]): string[] {
    const days = meetings.flatMap(meeting => 
      meeting.meeting_weekday.split(';').map(d => d.trim())
    );
    return [...new Set(days)];
  }

  static getUniqueCategories(meetings: Meeting[]): string[] {
    const categories = meetings
      .map(meeting => meeting.meeting_category)
      .filter((c): c is string => !!c);
    return [...new Set(categories)];
  }

  static getUniqueRegions(meetings: Meeting[]): string[] {
    const regions = meetings
      .map(meeting => meeting.meeting_region)
      .filter((r): r is string => !!r);
    return [...new Set(regions)];
  }

  static getUniquePeriods(meetings: Meeting[]): string[] {
    const periods = meetings.map(meeting => meeting.meeting_period).filter(Boolean);
    return [...new Set(periods)];
  }

  static getMeetingsByDay(meetings: Meeting[], day: string): Meeting[] {
    return meetings.filter(meeting => 
      (meeting.meeting_weekday || '').toLowerCase().includes(day.toLowerCase())
    );
  }

  static getMeetingsByTime(meetings: Meeting[], time: string): Meeting[] {
    return meetings.filter(meeting => meeting.meeting_time === time);
  }

  static getMeetingsByCategory(meetings: Meeting[], category: string): Meeting[] {
    return meetings.filter(meeting => meeting.meeting_category === category);
  }

  static searchMeetings(meetings: Meeting[], query: string): Meeting[] {
    if (!query || query.length < 2) return meetings;
    
    const lowerQuery = query.toLowerCase();
    return meetings.filter(meeting => 
      meeting.meeting_name.toLowerCase().includes(lowerQuery) ||
      meeting.meeting_category?.toLowerCase().includes(lowerQuery) ||
      meeting.meeting_place?.toLowerCase().includes(lowerQuery) ||
      meeting.meeting_region?.toLowerCase().includes(lowerQuery)
    );
  }
}
